/**
 * Every word on the page, in one file.
 *
 * The site says the same thing as the app, so the copy that exists in the product —
 * the nine goals, the four steps of the loop — is mirrored here rather than reinvented.
 * The goal KEYS are the real ones from the API; the phrasing is the design's, because
 * "I can't let go" is how someone arrives, and "Move On From Breakup" is how we file it.
 */

export const HERO = {
  /* Stored sentence-case: `.eyebrow` uppercases it, so the caps come from the design
     system rather than from the copy, and the tracking applies evenly. */
  eyebrow: 'Change your mind. Change your life.',
  /* The headline breaks after the accent, so "5 minutes" ends the first line. */
  titleLead: 'Maybe your mind just needs',
  titleAccent: '5 minutes',
  titleTail: 'instead of hours of scrolling.',
  lines: [
    'Simple daily audio experiences designed to help you build better habits, develop a healthier mindset, and create lasting change.',
  ],
  closing: 'Listen. Reflect. Practice. Repeat.',
  cta: 'Join the waitlist',
};

export const PROBLEM = {
  eyebrow: 'The problem',
  /* The closing full stop is rendered separately so it can carry the accent colour. */
  title: ['Your body is tired.', "Your mind isn't"],
  lines: [
    'You switch off the lights.',
    'You put your phone away.',
    'You close your eyes.',
    "And somehow… your brain decides it's the perfect time to replay everything.",
  ],
  reassure: "You don't need another thing to do.",
  accent: 'You might just need a moment to breathe.',
};

/**
 * The nine goals, worded as a feeling rather than a category. `key` must match the goals
 * table — it is what the waitlist stores and what a journey is tagged with.
 */
export const FEELINGS = [
  { key: 'OVERTHINKING', label: "I'm overthinking everything", tint: '#7C8CFF' },
  { key: 'MOVE_ON_FROM_BREAKUP', label: "I can't let go", tint: '#FF4D6D' },
  { key: 'MANAGE_ANGER', label: "I'm angry", tint: '#FFA53D' },
  { key: 'CONFIDENCE', label: "I don't feel good enough", tint: '#5AA9FF' },
  { key: 'WEALTH_MINDSET', label: "I'm worried about money", tint: '#3DD68C' },
  { key: 'DISCIPLINE', label: 'I feel stuck', tint: '#2FD4C4' },
  { key: 'RELATIONSHIPS', label: 'My relationship is hurting', tint: '#B06BFF' },
  { key: 'GOALS', label: 'I just want some peace', tint: '#A855F7' },
  { key: 'SLEEP', label: "I can't switch off at night", tint: '#8B93FF' },
] as const;

export const FEELINGS_SECTION = {
  eyebrow: 'Start where you are',
  title: 'What feels a bit too loud today?',
  aside: ['Whatever it is…', "You're not alone."],
};

/** CLAUDE.md §0's loop, said the way a person would say it. */
export const STEPS = [
  {
    key: 'CHOOSE',
    n: '01',
    tint: '#7C8CFF',
    title: 'Choose',
    body: "Find a story that feels close to what you're going through.",
  },
  {
    key: 'PLAY',
    n: '02',
    tint: '#2FD4E8',
    title: 'Press Play',
    body: 'Give yourself five quiet minutes. No scrolling. No notifications.',
  },
  {
    key: 'REFLECT',
    n: '03',
    tint: '#B06BFF',
    title: 'Reflect',
    body: "Listen. Feel. Notice something you hadn't noticed before.",
  },
  {
    key: 'RETURN',
    n: '04',
    tint: '#FF9F45',
    title: 'Come Back',
    body: 'One story at a time. One day at a time. One small shift at a time.',
  },
];

export const STEPS_SECTION = {
  eyebrow: 'How Manasil works',
  title: "You don't have to fix your whole life tonight.",
};

export const VOICE = {
  title: ['Some things are easier', 'to hear than to read.'],
  body: 'A small story can change the way you see things.',
  quote:
    '"Sometimes, the thing keeping you awake isn\'t the problem… it\'s the thoughts you keep carrying alone."',
  attribution: 'Kavya',
};

export const WAITLIST = {
  eyebrow: 'Be first',
  title: 'Manasil is almost ready.',
  body: 'Leave your details and we will tell you the day it opens — nothing else, no noise.',
  success: {
    title: "You're on the list.",
    body: "We'll be in touch when Manasil opens. Thank you for trusting us with a quiet minute.",
  },
};

export const FOOTER = {
  tagline: 'Change Your Mind, Change Your Life.',
  note: 'Manasil is a listening companion, not a substitute for medical or mental-health care.',
  company: {
    name: 'MANASIL PRIVATE LIMITED',
    address: 'Sf No 760/3B, 4B, 5B, Redhills Road, Ambattur, Tiruvallur 600053, Tamil Nadu, India',
    cin: 'U63122TN2026PTC198046',
    email: 'tech@manasil.in',
  },
};
